import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Radio, Select, Space, Table, message, type TablePaginationConfig } from 'antd';

import {
  deleteJogo,
  deletePassatempo,
  listCatalog,
  listDificuldades,
  type CatalogItem,
  type CatalogKind,
  type Dificuldade,
  type PaginatedResult,
} from '../../../services/admin';
import RegenerateCrosswordModal from '../RegenerateCrosswordModal';
import RegenerateSudokuModal from '../RegenerateSudokuModal';
import RegenerateWordsearchModal from '../RegenerateWordsearchModal';
import { CATALOG_KINDS, kindLabel } from './catalog-kind';
import { EdicaoSelect } from './EdicaoSelect';
import { GameInfoModal } from './GameInfoModal';
import { parseSearch } from './parse-search';
import { RowActions, catalogItemId } from './RowActions';
import { TemaSelect } from './TemaSelect';

type FilterableKind = Exclude<CatalogKind, 'other'>;

const emptyPage: PaginatedResult<CatalogItem> = { items: [], total: 0, page: 1, pageSize: 10 };
const ALL = 'all';

const titleOf = (item: CatalogItem): string | null =>
  item.source === 'passatempo' ? item.passatempo.titulo : item.jogo.titulo;

export const JogosTab: React.FC<{ onNewGame: () => void; reloadKey?: number }> = ({
  onNewGame,
  reloadKey = 0,
}) => {
  const [data, setData] = useState<PaginatedResult<CatalogItem>>(emptyPage);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [kind, setKind] = useState<FilterableKind | undefined>(undefined);
  const [search, setSearch] = useState<{ q?: string; id?: number }>({});
  const [edicao, setEdicao] = useState<{ id: number; nome: string | null } | null>(null);
  const [tema, setTema] = useState<{ id: number; nome: string | null } | null>(null);
  const edicaoId = edicao?.id;
  const temaId = tema?.id;
  const [dificuldadeId, setDificuldadeId] = useState<number | undefined>(undefined);
  const [dificuldades, setDificuldades] = useState<Dificuldade[]>([]);

  const [infoItem, setInfoItem] = useState<CatalogItem | null>(null);
  const [regenerating, setRegenerating] = useState<CatalogItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await listCatalog({
          page,
          pageSize,
          kind,
          ...search,
          edicoesId: edicaoId,
          temaId,
          dificuldadeId,
        }),
      );
    } catch {
      message.error('Falha ao carregar os jogos.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, kind, search, edicaoId, temaId, dificuldadeId]);

  // reloadKey recarrega a lista (ex.: depois de criar um jogo)
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    listDificuldades()
      .then(setDificuldades)
      .catch(() => message.error('Falha ao carregar as dificuldades.'));
  }, []);

  const handleDelete = async (item: CatalogItem) => {
    try {
      if (item.source === 'passatempo') await deletePassatempo(item.passatempo.id);
      else await deleteJogo(item.jogo.idJogo);
      message.success('Jogo excluído.');
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { msg?: string } } })?.response?.data?.msg;
      message.error(msg ?? 'Falha ao excluir o jogo.');
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? pageSize);
  };

  const columns = [
    { title: 'ID', key: 'id', width: 80, render: (_: unknown, item: CatalogItem) => `#${catalogItemId(item)}` },
    { title: 'Título', key: 'titulo', render: (_: unknown, item: CatalogItem) => titleOf(item) ?? '—' },
    { title: 'Tipo', key: 'tipo', render: (_: unknown, item: CatalogItem) => kindLabel(item.kind) },
    { title: 'Edição', key: 'edicao', render: (_: unknown, item: CatalogItem) => item.edicaoNome ?? '—' },
    { title: 'Tema', key: 'tema', render: (_: unknown, item: CatalogItem) => item.temaNome ?? '—' },
    {
      title: 'Ações',
      key: 'acoes',
      width: 200,
      render: (_: unknown, item: CatalogItem) => (
        <RowActions
          item={item}
          onInfo={setInfoItem}
          onRegenerate={setRegenerating}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  const regenPassatempo = regenerating?.source === 'passatempo' ? regenerating.passatempo : null;
  const regenJogo = regenerating?.source === 'jogo' ? regenerating.jogo : null;
  // idTipo do sudoku/caça-palavras vem do kind do catálogo
  const regenSudoku = regenerating?.kind === 'sudoku' ? regenJogo : null;
  const regenWordsearch = regenerating?.kind === 'wordsearch' ? regenJogo : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Jogos</h2>
        <Button type="primary" onClick={onNewGame}>
          + Novo jogo
        </Button>
      </div>

      <Radio.Group
        optionType="button"
        value={kind ?? ALL}
        onChange={(e) => {
          const value = e.target.value as string;
          setKind(value === ALL ? undefined : (value as FilterableKind));
          setPage(1);
        }}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value={ALL}>Todos</Radio.Button>
        {CATALOG_KINDS.map((k) => (
          <Radio.Button key={k} value={k}>
            {kindLabel(k)}
          </Radio.Button>
        ))}
      </Radio.Group>

      <Space size="middle" wrap style={{ marginBottom: 16, display: 'flex' }}>
        <Input.Search
          allowClear
          placeholder="Buscar por título ou #número"
          onSearch={(value) => {
            setSearch(parseSearch(value));
            setPage(1);
          }}
          style={{ width: 260 }}
        />
        <EdicaoSelect
          style={{ width: 200 }}
          value={edicaoId}
          valueLabel={edicao?.nome}
          onChange={(id, picked) => {
            setEdicao(id === undefined ? null : { id, nome: picked?.nome ?? null });
            setTema(null);
            setPage(1);
          }}
        />
        <TemaSelect
          style={{ width: 200 }}
          edicoesId={edicaoId}
          value={temaId}
          valueLabel={tema?.nome}
          onChange={(id, picked) => {
            setTema(id === undefined ? null : { id, nome: picked?.nome ?? null });
            setPage(1);
          }}
        />
        <Select
          allowClear
          placeholder="Dificuldade"
          style={{ width: 160 }}
          value={dificuldadeId}
          onChange={(value?: number) => {
            setDificuldadeId(value);
            setPage(1);
          }}
        >
          {dificuldades.map((d) => (
            <Select.Option key={d.id} value={d.id}>
              {d.nome}
            </Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        rowKey={(item) => `${item.source}-${catalogItemId(item)}`}
        loading={loading}
        columns={columns}
        dataSource={data.items}
        pagination={{ current: page, pageSize, total: data.total }}
        onChange={handleTableChange}
        scroll={{ x: 900 }}
      />

      <GameInfoModal item={infoItem} onClose={() => setInfoItem(null)} onSaved={load} />
      <RegenerateCrosswordModal target={regenPassatempo} onClose={() => setRegenerating(null)} onDone={load} />
      <RegenerateSudokuModal target={regenSudoku} onClose={() => setRegenerating(null)} onDone={load} />
      <RegenerateWordsearchModal target={regenWordsearch} onClose={() => setRegenerating(null)} onDone={load} />
    </div>
  );
};

export default JogosTab;
